import {
    IndicatorNotificationSubscriptionResponse,
    MachineHealthEventType,
    NotificationHardwareType,
    NotificationType,
} from '@kcftech/prometheus-client';
import { NotificationSubscriptionGroup } from 'custom_hooks/data_hooks/data_source/groupedNotificationSubscriptionsDataSource';
import { NotificationSubscription } from 'custom_hooks/data_hooks/data_source/notificationSubscriptionsDataSource';
import { notificationSubscriptionFormError } from 'pages/Settings/NotificationSubscriptions/form/shared';
import {
    SupportedNotificationType,
    TriggerSharedProps,
} from 'pages/Settings/NotificationSubscriptions/form/types';
import { Lookup } from 'utils/types';

export enum NotificationEventCategory {
    Warning = 'warning',
    Alarm = 'alarm',
    CriticalAlarmAll = 'criticalAlarmAll',
    CriticalAlarmTemperature = 'criticalAlarmTemperature',
}
const eventCategories: string[] = Object.values(NotificationEventCategory);

export const isNotificationEventCategory = (
    val: string,
): val is NotificationEventCategory => eventCategories.includes(val);

export const notificationEventCategoryName: Lookup<NotificationEventCategory, string> = {
    [NotificationEventCategory.Alarm]: 'Alarms',
    [NotificationEventCategory.Warning]: 'Warnings',
    [NotificationEventCategory.CriticalAlarmAll]:
        'Critical Alarms - All (Includes Temperature)',
    [NotificationEventCategory.CriticalAlarmTemperature]:
        'Critical Alarms - Temperature Only',
};

export const machineHealthEventTypeName: Lookup<MachineHealthEventType, string> = {
    [MachineHealthEventType.IssueCreate]: 'New Issues',
    [MachineHealthEventType.DraftIssueCreate]: 'Draft Issues',
    [MachineHealthEventType.IssueActivity]: 'All Activity on Published Issues',
};

export const notificationHardwareTypeName: Lookup<NotificationHardwareType, string> = {
    [NotificationHardwareType.Invalid]: 'Invalid',
    [NotificationHardwareType.BaseStation]: 'Offline Base Stations',
    [NotificationHardwareType.Repeater]: 'Offline Repeaters',
    [NotificationHardwareType.Hub]: 'Offline Hubs',
};

export const formattedNotificationTypes: Lookup<NotificationType, string> = {
    [NotificationType.Invalid]: 'Invalid',
    [NotificationType.Indicator]: 'Indicator Alerts',
    [NotificationType.Hardware]: 'Hardware Alerts',
    [NotificationType.HardwareHealth]: 'Hardware Issues',
    [NotificationType.MachineHealth]: 'Machine Health Issues',
};

// Hubs are excluded until they are supported
export type ValidNotificationHardwareType = Exclude<
    NotificationHardwareType,
    NotificationHardwareType.Hub | NotificationHardwareType.Invalid
>;

export const validNotificationHardwareTypes: ValidNotificationHardwareType[] = [
    NotificationHardwareType.BaseStation,
    NotificationHardwareType.Repeater,
];

export function validateNotificationTriggers(
    notificationTriggers: TriggerSharedProps[],
    userHasPhoneNumber?: boolean,
): string | undefined {
    if (notificationTriggers.length < 1) {
        throw Error('something broke, form should never have 0 triggers');
    }
    if (!notificationTriggers.some(hasEnabledNotificationMethods)) {
        return 'At least one notification method must be enabled';
    }

    const hasPhoneNumberError =
        !userHasPhoneNumber &&
        Object.values(notificationTriggers).some((nt) => nt.IsSmsEnabled);

    if (hasPhoneNumberError) {
        return notificationSubscriptionFormError.userHasNoPhoneNumber;
    }
    return undefined;
}

export const hasEnabledNotificationMethods = (nt: TriggerSharedProps) =>
    nt.IsEmailEnabled || nt.IsSmsEnabled || nt.IsSdConnectPushEnabled;

export const notificationTypeToTabName: Lookup<SupportedNotificationType, string> = {
    [NotificationType.Indicator]: 'indicatorAlerts',
    [NotificationType.Hardware]: 'hardware',
    [NotificationType.HardwareHealth]: 'hardwareHealth',
    [NotificationType.MachineHealth]: 'machineHealth',
};

export const getNotificationTypeDisplayName = (
    ns: NotificationSubscription | NotificationSubscriptionGroup,
) => formattedNotificationTypes[ns.discriminator];

export const tabNameToNotificationType = (tabName: string | undefined) => {
    switch (tabName) {
        case notificationTypeToTabName[NotificationType.Indicator]:
            return NotificationType.Indicator;
        case notificationTypeToTabName[NotificationType.Hardware]:
            return NotificationType.Hardware;
        default:
            return undefined;
    }
};

export const isIndicatorAlertsSubscription = (
    value: NotificationSubscription,
): value is IndicatorNotificationSubscriptionResponse =>
    value.discriminator === 'Indicator';

export const getSubscriptionGroupId = (
    ns: NotificationSubscription | NotificationSubscriptionGroup,
): string | undefined =>
    ns.discriminator === 'Indicator' || ns.discriminator === 'MachineHealth'
        ? ns.Group?.GroupId
        : undefined;

export const getNotificationSubscriptionRowKey = (
    nsg: NotificationSubscription | NotificationSubscriptionGroup,
): string => {
    const groupId = getSubscriptionGroupId(nsg);
    const id = groupId ?? nsg.Location.LocationId;
    return id + nsg.discriminator;
};
